import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../Global/Layout';
import { API_CONFIG } from '../../../contexts/constants';
import { showError } from '../../../utils/sweetAlertConfig';
import { formatDateTimeDefault } from '../../../utils/dateFormatter';

// Pagination configuration
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const List = ({ onLogout, userRole }) => {
  const [newsPromotions, setNewsPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNewsPromotions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  	if (!['sysadmin', 'owner', 'admin', 'agent', 'customer'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

  const fetchNewsPromotions = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage,
        pageSize: pageSize,
		scope: 'newest'
      });

      const response = await axios.get(`${API_CONFIG.BASE_URL}/news?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data && Array.isArray(response.data.data)) {
        setNewsPromotions(response.data.data);
        if (response.data.pagination) {
          setTotalItems(response.data.pagination.total);
        }
      } else {
        setNewsPromotions([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error fetching news promotions:', error);
      showError('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข่าวสารได้');
      setNewsPromotions([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Calculate pagination
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  const gotoPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
  };

  return (
    <Layout title="ข่าวสารและโปรโมชั่น" onLogout={onLogout} userRole={userRole}>
      <div className="mb-4">
        <p className="text-gray-600 dark:text-gray-400">
          ข่าวสารและโปรโมชั่นล่าสุดทั้งหมด
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">กำลังโหลดข่าวสาร...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {newsPromotions.map((item) => (
              <div
                key={item.id}
                className={`bg-white dark:bg-surface-dark border-l-4 rounded-lg shadow-md p-4 transition-all hover:shadow-lg cursor-pointer ${
                  item.priority === 'high'
                    ? 'border-l-red-500'
                    : item.priority === 'medium'
                    ? 'border-l-yellow-500'
                    : 'border-l-blue-500'
                }`}
                onClick={() => navigate(`/news/views/${item.id}`)}
              >
                <div className="flex items-start justify-between mb-2">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      item.type === 'promotion'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                    }`}
                  >
                    {item.badge}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{formatDateTimeDefault(item.date)}</span>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-2" style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {item.title}
                </h3>

                <div
                  className="text-sm text-gray-600 dark:text-gray-300 mb-3"
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
					maxHeight: '4.5em',
					overflow: 'auto'
                  }}
                  dangerouslySetInnerHTML={{ __html: item.content }}
                />

                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center">
                    {item.type === 'promotion' ? (
                      <svg className="w-4 h-4 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-blue-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    )}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {item.type === 'promotion' ? 'โปรโมชั่น' : 'ข่าวสาร'}
                    </span>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/news/views/${item.id}`);
                    }}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    อ่านเพิ่มเติม →
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
			<div className="bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark p-4">
				<div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-4">
					<div className='flex flex-wrap items-center justify-center gap-2'>
						<div className="flex items-center gap-2">
							<select
							id="pageSize"
							value={pageSize}
							onChange={(e) => {
								setPageSize(Number(e.target.value));
								setCurrentPage(1);
							}}
							className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
							>
							{PAGE_SIZE_OPTIONS.map(size => (
								<option key={size} value={size}>{size}</option>
							))}
							</select>
						</div>
						<div className="flex flex-wrap items-center justify-center gap-1">
							<button
								onClick={() => gotoPage(1)}
								disabled={safeCurrentPage === 1}
								className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm"
							>
								« หน้าแรก
							</button>
							<button
								onClick={() => gotoPage(safeCurrentPage - 1)}
								disabled={safeCurrentPage === 1}
								className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm"
							>
								‹ ก่อนหน้า
							</button>

							{Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
								let pageNum;
								if (totalPages <= 5) {
								pageNum = i + 1;
								} else if (safeCurrentPage <= 3) {
								pageNum = i + 1;
								} else if (safeCurrentPage >= totalPages - 2) {
								pageNum = totalPages - 4 + i;
								} else {
								pageNum = safeCurrentPage - 2 + i;
								}

								return (
								<button
									key={pageNum}
									onClick={() => gotoPage(pageNum)}
									className={`min-w-[36px] px-2 py-1 border rounded transition text-sm ${
									safeCurrentPage === pageNum
										? 'bg-blue-500 border-blue-500 text-white font-semibold'
										: 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
									}`}
								>
									{pageNum}
								</button>
								);
							})}

							<button
								onClick={() => gotoPage(safeCurrentPage + 1)}
								disabled={safeCurrentPage === totalPages}
								className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm"
							>
								ถัดไป ›
							</button>
							<button
								onClick={() => gotoPage(totalPages)}
								disabled={safeCurrentPage === totalPages}
								className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm"
							>
								สุดท้าย »
							</button>

						</div>
					</div>

					<div className="text-sm text-gray-600 dark:text-gray-400">
					แสดง {startIndex + 1} ถึง {Math.min(endIndex, totalItems)} จาก {totalItems} รายการ
					</div>
				</div>
			</div>
        </>
      )}
    </Layout>
  );
};

export default List;
